import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import LayersIcon from '@material-ui/icons/Layers';
import { useStyles } from './styles';
import {
  Paper,
  Tooltip,
  Snackbar,
  SnackbarContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';

export const MicrosoftSyncGroup: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // const [dialogData, setDialogData] = useState<any[]>([]);
  const {
    MicrosoftStore: { microsoftsyncGroup, allsyncGroupsView, microsoftreSyncSingleGroup, microsoftreSyncGroup },
    ldapStore: { getGroups, groupsView },
    notificationStore: { openNotification },
  } = useStore();
  const [syncedGroups, setSyncedGroups] = useState(new Set());
  const microsoftsyncGroupReq = useQuery('microsoftsyncGroup', () => microsoftsyncGroup(), {
    onError: () => openNotification('error', `We are unable to get the group from Microsoft`),
  });
  const getGroupsReq = useQuery('getGroups', getGroups, {
    onError: () => openNotification('error', `Can't get group`),
  });
  // const microsoftreSyncGroupReq = useMutation(microsoftreSyncGroup, {
  //   mutationKey: 'microsoftreSyncGroup',
  //   onSuccess: () => {
  //     setOpen(false);
  //     setSyncedGroups(new Set(allsyncGroupsView.map((group) => group.email)));
  //     openNotification('success', 'Bi-direction Sync Successful');
  //     microsoftsyncGroupReq.refetch();
  //     getGroupsReq.refetch();
  //   },
  //   onError: ({ message }: Error) => {
  //     setOpen(false);
  //     openNotification('error', message);
  //   },
  // });

  const microsoftreSyncGroupReq = useMutation(
    () =>
      microsoftreSyncGroup({
        sync_users: false,
        sync_groups: true,
        google_to_ldap: true,
        ldap_to_google: true,
      }),
    {
      mutationKey: 'microsoftreSyncGroupReq',
      onSuccess: () => {
        setOpen(false);
        openNotification('success', 'Bi-direction Sync Successful');
        microsoftsyncGroupReq.refetch();
        getGroupsReq.refetch();
      },
      onError: ({ message }: Error) => {
        setOpen(false);
        openNotification('error', message);
      },
    }
  );
  const microsoftreSyncSingleUserReq = useMutation((data: { id: string }) => microsoftreSyncSingleGroup(data), {
    mutationKey: 'microsoftreSyncSingleUser',
    onSuccess: (_, variables) => {
      openNotification('success', 'Selected Users sync’ed with App Microsoft Successfully');
      setSyncedGroups((prev) => new Set(prev).add(variables.id));
      microsoftsyncGroupReq.refetch();
      getGroupsReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      openNotification('error', `We are unable to sync the group from microsoft`);
    },
  });
  const group = groupsView.map((item: { microsoft_id: any }) => item.microsoft_id);
  const filteredUserSet = new Set(group);
  const microsoftGroupCount = allsyncGroupsView.length ?? 0;
  const ldapGroupCount = groupsView.length ?? 0;
  // const difference = Math.abs(ldapGroupCount - microsoftGroupCount);
  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncGroup';
  }, []);
  const rows = useMemo(() => {
    return !microsoftsyncGroupReq.isFetching ? allsyncGroupsView : [];
  }, [microsoftsyncGroupReq.isFetching, allsyncGroupsView]);
  const handleSync = (id: string) => {
    microsoftreSyncSingleUserReq.mutate({ id });
  };
  const handleSyncAll = () => {
    // Show dialog with data first
    // setDialogData(allsyncGroupsView);
    setDialogOpen(true);
  };

  const confirmSyncAll = () => {
    setDialogOpen(false);
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    microsoftreSyncGroupReq.mutate();
    setOpen(true);
  };
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  useAppPreloader(
    microsoftsyncGroupReq.isFetching ||
      microsoftreSyncSingleUserReq.isLoading ||
      microsoftreSyncGroupReq.isLoading ||
      getGroupsReq.isFetching
  );
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <LayersIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'cn',
      headerName: 'NAME',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.displayName || ''}>
          <div>
            <span>{params.row.displayName}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'description',
      headerName: 'DESCRIPTION',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.description}>
          <div>
            <span>{params.row.description}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          {!filteredUserSet.has(params.row.microsoftId) && !syncedGroups.has(params.row.microsoftId) ? (
            <Tooltip title='Not Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.microsoftId)}>
                <SyncDisabledIcon style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.microsoftId)}>
                <SyncIcon style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      {!microsoftsyncGroupReq.isFetching && !getGroupsReq.isFetching && (
        <Paper className={classes.dataGrid} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapGroupCount} Groups in EasyIdentity </h1>
            <h1 className={classes.textBox}>
              You have {microsoftGroupCount} Groups in Microsoft Entra / Office 365 Workspace
            </h1>
            <div style={{ display: 'flex' }}>
              <h1 className={classes.textBox}>
                Sync Groups : EasyIdentity {'<->'} Microsoft Entra / Office 365 Workspace
                <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
              </h1>
            </div>
          </>
        </Paper>
      )}
      <Paper className={classes.dataGrid} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>
            {' '}
            List of All Microsoft Entra / Office 365 Workspace Groups
          </h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm'>
        <DialogTitle>Sync All Groups Confirmation</DialogTitle>
        <DialogContent>
          <Typography variant='body1' gutterBottom>
            Do you want to sync all groups?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={confirmSyncAll} color='primary' variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
